<script setup>
// Auto-extracted chrome for SolarMillennium.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Solar Millennium" },
    brandTagline: { type: String, default: "PV-Portfolio. Klar gerechnet." },
    brandAccent: { type: String, default: '#0EA5E9' },
    brandCta: { type: String, default: '#000' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Solar Millennium");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "PV-Portfolio. Klar gerechnet.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');
const brandCta = computed(() => props.brandCta);

const fontsHref = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=Inter:wght@400;500;600;700;800&display=swap";
</script>

<template>
    <div class="smi" :style="`--smi-accent:${brandAccent};--smi-cta:${brandCta};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="smi-bar">
                    <div class="smi-bar__inner">
                        <a href="/" class="smi-mark" aria-label="Solar Millennium Start">
                            <span class="smi-mark__rule"></span>
                            <span class="smi-mark__name">{{ brandName }}</span>
                            <span class="smi-mark__sep">/</span>
                            <span class="smi-mark__sub">{{ brandTagline }}</span>
                        </a>
                        <a href="#smi-flow" class="smi-bar__cta">ROI berechnen
                            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 7h7M7 4l3 3-3 3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </a>
                    </div>
                </header>
        <slot />
        <footer class="smi-foot">
                    <div class="smi-foot__inner">
                        <p>{{ brandName }} &middot; {{ brandTagline }}</p>
                        <nav><a href="/impressum">Impressum</a><a href="/datenschutz">Datenschutz</a><a href="/agb">AGB</a><a href="/widerruf">Widerruf</a><a href="/ratgeber">Ratgeber</a></nav>
                        <p class="smi-foot__copy">&copy; {{ new Date().getFullYear() }} solar-millennium.de &middot; alle Berechnungen ohne Gewähr.</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.smi { --smi-ink: #0f172a; --smi-sub: #475569; --smi-line: #e2e8f0; --smi-bg: #f8fafc; --smi-paper: #ffffff;
    font-family: 'Inter', system-ui, sans-serif; color: var(--smi-ink); background: var(--smi-bg); min-height: 100dvh;
    font-feature-settings: 'ss01', 'cv11'; line-height: 1.55; -webkit-font-smoothing: antialiased; }
.smi *, .smi *::before, .smi *::after { box-sizing: border-box; }
.smi h1, .smi h2, .smi h3, .smi h4 { font-family: 'Fraunces', Georgia, serif; font-weight: 700; letter-spacing: -0.022em; line-height: 1.05; margin: 0; color: var(--smi-ink); }
.smi h1 em, .smi h2 em { font-style: italic; color: var(--smi-cta); font-weight: 700; }
.smi p { margin: 0; }
.smi-num-tab { font-variant-numeric: tabular-nums; font-feature-settings: 'tnum'; }

/* Top bar */
.smi-bar { border-bottom: 1px solid var(--smi-line); background: var(--smi-paper); position: sticky; top: 0; z-index: 30; }
.smi-bar__inner { max-width: 1200px; margin: 0 auto; padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.smi-mark { display: inline-flex; align-items: center; gap: 10px; color: var(--smi-ink); text-decoration: none; font-size: 14px; }
.smi-mark__rule { display: inline-block; width: 22px; height: 2px; background: var(--smi-accent); }
.smi-mark__name { font-weight: 700; letter-spacing: -0.01em; }
.smi-mark__sep { color: var(--smi-sub); }
.smi-mark__sub { color: var(--smi-sub); }
.smi-bar__cta { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: var(--smi-cta); text-decoration: none; padding: 8px 14px; border: 1px solid var(--smi-line); border-radius: 999px; transition: all 200ms cubic-bezier(0.32,0.72,0,1); }
.smi-bar__cta:hover { border-color: var(--smi-cta); }

/* Hero */
.smi-hero { padding: 56px 20px 40px; }
.smi-hero__inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 40px; align-items: end; }
@media (min-width: 920px) { .smi-hero__inner { grid-template-columns: 1.3fr 1fr; gap: 64px; } .smi-hero { padding: 88px 24px 56px; } }
.smi-hero__chip { color: var(--smi-sub); text-transform: uppercase; font-size: 11px; letter-spacing: 0.18em; margin-bottom: 14px; font-weight: 600; }
.smi-hero__h { font-size: clamp(38px, 6.8vw, 76px); margin-bottom: 16px; text-wrap: balance; }
.smi-hero__sub { color: var(--smi-sub); font-size: 17px; max-width: 56ch; margin-bottom: 26px; }
.smi-hero__row { display: flex; flex-wrap: wrap; align-items: center; gap: 18px; }
.smi-hero__meta { font-size: 13px; color: var(--smi-sub); }
.smi-hero__panel { background: var(--smi-paper); border: 1px solid var(--smi-line); border-radius: 18px; padding: 28px; }
.smi-panel__eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: 0.16em; color: var(--smi-sub); font-weight: 600; margin-bottom: 12px; }
.smi-panel__big { font-family: 'Fraunces', serif; font-size: 64px; font-weight: 700; letter-spacing: -0.03em; line-height: 1; color: var(--smi-accent); }
.smi-panel__unit { font-size: 22px; color: var(--smi-sub); margin-left: 6px; font-weight: 500; }
.smi-panel__hint { font-size: 13px; color: var(--smi-sub); margin-top: 8px; }
.smi-panel__rule { border: none; border-top: 1px solid var(--smi-line); margin: 18px 0; }
.smi-panel__list { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
.smi-panel__list li { display: flex; justify-content: space-between; font-size: 14px; color: var(--smi-sub); }
.smi-panel__list li span:last-child { color: var(--smi-ink); font-weight: 600; }

/* Trust strip */
.smi-strip { max-width: 1200px; margin: 56px auto 0; padding: 0 20px; display: grid; grid-template-columns: 1fr; gap: 18px; }
@media (min-width: 720px) { .smi-strip { grid-template-columns: repeat(3, 1fr); gap: 0; border-top: 1px solid var(--smi-line); padding-top: 20px; } }
.smi-strip > div { display: flex; flex-direction: column; gap: 4px; font-size: 13px; padding-right: 24px; }
.smi-strip > div span:first-child { color: var(--smi-sub); text-transform: uppercase; letter-spacing: 0.14em; font-size: 11px; font-weight: 600; }
.smi-strip > div span:last-child { color: var(--smi-ink); font-weight: 500; }

/* Flow */
.smi-flow { padding: 64px 20px; }
.smi-flow__inner { max-width: 760px; margin: 0 auto; }
.smi-flow__head { margin-bottom: 28px; }
.smi-flow__chip { font-size: 11px; text-transform: uppercase; letter-spacing: 0.18em; color: var(--smi-sub); font-weight: 600; margin-bottom: 10px; }
.smi-flow__h { font-size: clamp(28px, 4vw, 40px); }
.smi-flow__kicker { color: var(--smi-sub); margin-top: 8px; font-size: 15px; }
.smi-flow__prog { margin-top: 18px; height: 2px; background: var(--smi-line); position: relative; overflow: hidden; }
.smi-flow__prog span { position: absolute; inset: 0 auto 0 0; background: var(--smi-cta); transition: width 320ms cubic-bezier(0.32,0.72,0,1); }

.smi-card { background: var(--smi-paper); border: 1px solid var(--smi-line); border-radius: 14px; padding: 28px; }
.smi-field { display: flex; flex-direction: column; gap: 14px; }
.smi-lbl { font-weight: 600; font-size: 15px; color: var(--smi-ink); }
.smi-hint { font-size: 13px; color: var(--smi-sub); }
.smi-input, .smi-in input { width: 100%; padding: 14px 16px; font-size: 16px; border: 1px solid var(--smi-line); border-radius: 10px; background: var(--smi-bg); font-family: inherit; color: var(--smi-ink); transition: border-color 180ms ease; }
.smi-input:focus, .smi-in input:focus { outline: none; border-color: var(--smi-accent); }
.smi-input-row { display: flex; align-items: stretch; border: 1px solid var(--smi-line); border-radius: 10px; overflow: hidden; background: var(--smi-bg); }
.smi-input-row input { border: none; background: transparent; }
.smi-input-row__unit { display: flex; align-items: center; padding: 0 14px; color: var(--smi-sub); font-size: 14px; border-left: 1px solid var(--smi-line); }
.smi-slider { display: flex; align-items: center; gap: 16px; }
.smi-slider input[type=range] { flex: 1; accent-color: var(--smi-accent); }
.smi-slider output { font-weight: 600; min-width: 80px; text-align: right; }
.smi-toggle { display: grid; gap: 12px; }
.smi-toggle__opt { text-align: left; background: var(--smi-bg); border: 1px solid var(--smi-line); border-radius: 12px; padding: 18px; cursor: pointer; transition: all 180ms ease; display: flex; flex-direction: column; gap: 6px; font-family: inherit; }
.smi-toggle__opt:hover { border-color: var(--smi-accent); }
.smi-toggle__opt--on { border-color: var(--smi-cta); background: #fff; box-shadow: inset 0 0 0 1px var(--smi-cta); }
.smi-toggle__h { font-weight: 700; font-size: 15px; color: var(--smi-ink); }
.smi-toggle__sub { color: var(--smi-sub); font-size: 14px; }

.smi-card__nav { display: flex; justify-content: space-between; align-items: center; margin-top: 24px; gap: 12px; }

/* Buttons */
.smi-btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; font-family: inherit; font-weight: 600; font-size: 15px; padding: 12px 22px; border-radius: 999px; border: 1px solid transparent; cursor: pointer; transition: all 220ms cubic-bezier(0.32,0.72,0,1); text-decoration: none; }
.smi-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.smi-btn--cta { background: var(--smi-cta); color: #fff; }
.smi-btn--cta:hover:not(:disabled) { background: #c2410c; transform: translateY(-1px); }
.smi-btn--cta:active:not(:disabled) { transform: translateY(0); }
.smi-btn--ghost { background: transparent; color: var(--smi-sub); border-color: var(--smi-line); }
.smi-btn--ghost:hover { color: var(--smi-ink); border-color: var(--smi-ink); }
.smi-btn--lg { padding: 16px 28px; font-size: 16px; }
.smi-btn--sm { padding: 8px 16px; font-size: 13px; }
.smi-btn--full { width: 100%; }
.smi-btn__arr { transition: transform 200ms ease; }
.smi-btn:hover .smi-btn__arr { transform: translateX(2px); }

/* Result */
.smi-result__eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: 0.16em; color: var(--smi-sub); font-weight: 600; margin-bottom: 8px; }
.smi-result__big { font-family: 'Fraunces', serif; font-size: clamp(56px, 9vw, 96px); font-weight: 700; letter-spacing: -0.035em; line-height: 1; color: var(--smi-cta); margin-bottom: 6px; }
.smi-result__unit { font-size: 24px; color: var(--smi-sub); margin-left: 8px; font-weight: 500; font-family: 'Inter', sans-serif; }
.smi-result__hint { color: var(--smi-sub); font-size: 15px; margin-bottom: 24px; }
.smi-kpis { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 0 0 24px; padding: 24px 0; border-top: 1px solid var(--smi-line); border-bottom: 1px solid var(--smi-line); }
.smi-kpis div { display: flex; flex-direction: column; gap: 4px; }
.smi-kpis dt { font-size: 12px; color: var(--smi-sub); text-transform: uppercase; letter-spacing: 0.12em; font-weight: 600; }
.smi-kpis dd { margin: 0; font-size: 22px; font-weight: 700; color: var(--smi-ink); }
.smi-kpis__win { color: #15803d; }
.smi-method { margin-top: 20px; }
.smi-method summary { cursor: pointer; color: var(--smi-sub); font-size: 14px; font-weight: 500; }
.smi-method ul { margin: 12px 0 0; padding-left: 18px; color: var(--smi-sub); font-size: 14px; display: grid; gap: 6px; }

/* Lead */
.smi-lead { margin-top: 36px; padding-top: 32px; border-top: 1px solid var(--smi-line); }
.smi-lead__eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: 0.18em; color: var(--smi-sub); font-weight: 600; margin-bottom: 8px; }
.smi-lead__h { font-size: clamp(22px, 3vw, 28px); margin-bottom: 8px; }
.smi-lead__sub { color: var(--smi-sub); margin-bottom: 20px; }
.smi-form { display: grid; gap: 14px; }
.smi-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.smi-in { display: flex; flex-direction: column; gap: 6px; }
.smi-in > span { font-size: 13px; font-weight: 600; color: var(--smi-ink); }
.smi-in em { font-weight: 400; color: var(--smi-sub); font-style: normal; }
.smi-in--err input { border-color: #b91c1c; }
.smi-in__err { color: #b91c1c; font-size: 13px; }
.smi-in__err--center { text-align: center; }
.smi-consent { display: flex; gap: 12px; padding: 16px; background: var(--smi-bg); border: 1px solid var(--smi-line); border-radius: 10px; font-size: 13px; color: var(--smi-sub); line-height: 1.5; cursor: pointer; }
.smi-consent input { margin-top: 2px; flex-shrink: 0; accent-color: var(--smi-cta); }

.smi-done { text-align: center; padding: 40px 0; }
.smi-done__big { font-family: 'Fraunces', serif; font-size: 36px; font-weight: 700; margin-bottom: 8px; }

/* Methodology */
.smi-meth { background: #0f172a; color: #f1f5f9; padding: 80px 20px; }
.smi-meth__inner { max-width: 980px; margin: 0 auto; }
.smi-meth h2 { color: #fff; }
.smi-meth h2 em { color: #fb923c; }
.smi-meth__chip { color: #94a3b8; text-transform: uppercase; letter-spacing: 0.18em; font-size: 11px; font-weight: 600; margin-bottom: 14px; }
.smi-meth__h { font-size: clamp(30px, 4.5vw, 48px); max-width: 22ch; margin-bottom: 40px; }
.smi-meth__list { list-style: none; padding: 0; margin: 0; display: grid; gap: 28px; }
.smi-meth__list li { display: grid; grid-template-columns: auto 1fr auto; gap: 24px; padding-bottom: 28px; border-bottom: 1px solid #1e293b; align-items: start; }
.smi-meth__list li:last-child { border-bottom: 0; }
.smi-meth__no { font-family: 'Fraunces', serif; font-size: 36px; color: #fb923c; line-height: 1; font-weight: 700; }
.smi-meth__list h3 { color: #fff; font-size: 20px; margin-bottom: 6px; }
.smi-meth__list p { color: #cbd5e1; font-size: 15px; }
.smi-meth__tag { font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; padding: 4px 10px; border-radius: 999px; background: #1e293b; color: #cbd5e1; align-self: start; font-weight: 600; }
@media (max-width: 720px) { .smi-meth__list li { grid-template-columns: auto 1fr; gap: 16px; } .smi-meth__tag { grid-column: 2; justify-self: start; } }

/* FAQ */
.smi-faq { padding: 80px 20px; }
.smi-faq__inner { max-width: 1100px; margin: 0 auto; }
.smi-faq__chip { color: var(--smi-sub); text-transform: uppercase; letter-spacing: 0.18em; font-size: 11px; font-weight: 600; margin-bottom: 28px; }
.smi-faq__grid { display: grid; grid-template-columns: 1fr; gap: 28px; }
@media (min-width: 720px) { .smi-faq__grid { grid-template-columns: repeat(3, 1fr); gap: 40px; } }
.smi-faq__item h4 { font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 700; margin-bottom: 8px; color: var(--smi-ink); }
.smi-faq__item p { color: var(--smi-sub); font-size: 15px; }

/* Footer */
.smi-foot { background: #0f172a; color: #94a3b8; padding: 32px 20px; }
.smi-foot__inner { max-width: 1200px; margin: 0 auto; display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 16px; font-size: 13px; }
.smi-foot nav { display: flex; gap: 18px; }
.smi-foot a { color: #cbd5e1; text-decoration: none; }
.smi-foot a:hover { color: #fff; }
.smi-foot__copy { font-size: 12px; opacity: 0.7; }

/* Sticky CTA */
.smi-stick { position: fixed; left: 0; right: 0; bottom: 0; padding-bottom: env(safe-area-inset-bottom); background: rgba(15, 23, 42, 0.96); backdrop-filter: blur(8px); z-index: 40; }
.smi-stick__inner { max-width: 1200px; margin: 0 auto; padding: 14px 20px; display: flex; justify-content: space-between; align-items: center; gap: 14px; color: #fff; font-size: 14px; }
@media (min-width: 920px) { .smi-stick { display: none; } }

@media (prefers-reduced-motion: reduce) {
    .smi *, .smi *::before, .smi *::after { transition: none !important; animation: none !important; }
}

/* ── Trust strip (images + cites + byline) — smi-* unique treatment ── */
.smi-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.smi-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .smi-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.smi-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 0px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 0; box-shadow: 0 0 0 1px rgba(20,17,13,0.10), 12px 12px 0 0 #1E293B; 
}
.smi-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.smi-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.smi-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.smi-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.smi-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.smi-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #1E293B55;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.smi-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.smi-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #1E293B;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.smi-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.smi-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.smi-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.smi-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.smi-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.smi-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
